import React, { Component } from "react";
import { AppCard, DataTableToolbar, PageLayout } from "../components/ui";

import _ from "lodash";
import { Journaling } from "../models/Journaling";
import { dateBodyTemplate } from "../utils/columnTemplates";
import { journalingsApiService } from "../services/api/journalings/JournalingApiService";
import JournalingDetailsDialog from "./Journaling/JournalingDetails";
import { toastService } from "../services/toastService";
import { SearchRequestBaseModel, SortOrderType } from "../models/SearchRequestModel";
import ExtendedDataTable from "../components/ExtendedDataTable";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { dateFilterTemplate, numberFilterMatchModeOptions, stringFilterMatchModeOptions } from "./Paginator";
import { merchantApiService } from "../services/api/merchants/MerchantApiService";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "../models/SelectItem";
import { authUsersApiService } from "../services/api/clients/AuthUsersApiService";
import { utils } from "../utils/utils";
import { BookOpen } from "lucide-react";

interface JournalingsState {
    journaling: Journaling[];
    isLoading: boolean;
    totalRecords: number;
    viewjournalingDetails: boolean;
    selectedJournaling: Journaling | null;
    merchants: SelectItem[];
    users: SelectItem[];
}

interface JournalingComponentProps {
    filterFunction?: (searchRequest: SearchRequestBaseModel) => void;
    embedded?: boolean;
}

export default class JournalingComponent extends Component<JournalingComponentProps, JournalingsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: JournalingComponentProps | Readonly<JournalingComponentProps>) {
        super(props);

        this.state = {
            journaling: [],
            isLoading: false,
            totalRecords: 0,
            viewjournalingDetails: false,
            selectedJournaling: null,
            merchants: [],
            users: [],
        };
    }

    loadJournalingData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);
        this.props.filterFunction && this.props.filterFunction(searchRequest);

        const journalingResult = await journalingsApiService.getAll(searchRequest);
        if (this.state.merchants && this.state.merchants.length == 0) {
            const merchantResult = await merchantApiService.getSelectItem();
            if (merchantResult.success) {
                this.setState({ merchants: merchantResult.data });
            }
        }
        if (this.state.users && this.state.users.length == 0) {
            const usersResult = await authUsersApiService.getSelectItem();
            if (usersResult.success) {
                this.setState({ users: usersResult.data });
            }
        }

        if (!journalingResult.success) {
            toastService.error(journalingResult.message);
        }
        this.setState({ isLoading: false, journaling: journalingResult.data, totalRecords: journalingResult.totalCount });
    };

    showJournalingDetails = (rowData: Journaling) => {
        this.setState({ viewjournalingDetails: true, selectedJournaling: rowData });
    };

    hideJournalingDetailsView = () => {
        this.setState({ viewjournalingDetails: false });
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => this.dataTable!.reset()} hasActiveFilters={hasActiveFilters} />;

    onRowClick = (event: { data: Journaling }) => {
        this.showJournalingDetails(event.data);
    };

    merchantFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.merchants}
                onChange={(e) => options.filterCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                filter
                showClear
                placeholder="Select Merchants"
                className="p-column-filter"
            />
        );
    };

    usersFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.users}
                onChange={(e) => options.filterCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                filter
                showClear
                placeholder="Select Users"
                className="p-column-filter"
            />
        );
    };

    renderContent() {
        return (
            <>
                {this.state.viewjournalingDetails && this.state.selectedJournaling != null && (
                    <JournalingDetailsDialog
                        isVisible={this.state.viewjournalingDetails}
                        onHide={this.hideJournalingDetailsView}
                        journaling={this.state.selectedJournaling}
                        editable={false}
                    />
                )}
                <ExtendedDataTable
                                            header={this.renderHeader}
                                            value={this.state.journaling}
                                            loading={this.state.isLoading}
                                            totalRecords={this.state.totalRecords}
                                            onChange={this.loadJournalingData}
                                            ref={(ref) => (this.dataTable = ref)}
                                            defaultSortOrder={SortOrderType.Descending}
                                            onRowClick={this.onRowClick}
                                            defaultSortField="madeOn"
                                        >
                                            <Column {...({ alwaysVisible: true } as any)} field="id" header="Id" filterMatchModeOptions={numberFilterMatchModeOptions}></Column>
                                            <Column {...({ alwaysVisible: true } as any)}
                                                field="application"
                                                header="Application"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                            <Column {...({ alwaysVisible: true } as any)}
                                                field="madeOn"
                                                header="Event Date"
                                                body={dateBodyTemplate("madeOn")}
                                                dataType="date"
                                                filterElement={dateFilterTemplate}
                                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                            ></Column>
                                            <Column
                                                field="madeBy"
                                                header="User"
                                                filterMatchMode="equals"
                                                filterField="madeBy"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                                filterElement={this.usersFilterTemplate}
                                                body={(rowData: Journaling) => (
                                                    <p>{this.state.users.find((a) => a.value == rowData.madeBy)?.label}</p>
                                                )}
                                            ></Column>
                                            <Column
                                                field="tenantId"
                                                header="Merchant"
                                                filterMatchMode="equals"
                                                filterField="tenantId"
                                                showFilterMatchModes={false}
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                                filterElement={this.merchantFilterTemplate}
                                                body={(rowData: Journaling) => (
                                                    <p>{this.state.merchants.find((a) => a.value == rowData.tenantId)?.label}</p>
                                                )}
                                            ></Column>
                                            <Column field="objectType" header="Event" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                                            <Column
                                                field="operationType"
                                                header="Event Type"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                            <Column
                                                field="madeFromIPAddress"
                                                header="Ip Address"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                        </ExtendedDataTable>
            </>
        );
    }

    render() {
        if (this.props.embedded) {
            return this.renderContent();
        }

        return (
            <PageLayout heading="Journaling" icon={BookOpen}>
                <AppCard className="mb-3">
                    <AppCard.Body className="!p-0">
                        {this.renderContent()}
                    </AppCard.Body>
                </AppCard>
            </PageLayout>
        );
    }
}
